/* selv-memoire.js · Mémoire de parfum (18 oct. 2026). Vanilla, sans bibliothèque, différé, sur toutes les pages
   (hors éditeur de thème ; réglage settings.selv_memoire). Rien sur le réseau avant le temps libre qui suit load.
   1. Consentement (Loi 25) : rien n'est écrit tant que Shopify.customerPrivacy.preferencesProcessingAllowed() n'est pas
      vrai (API chargée en temps libre par Shopify.loadFeatures si le bandeau ne l'a pas déjà chargée). Jamais de lecture
      des témoins de Shopify (la documentation l'interdit). À chaque page, dès que l'API répond « non » : la mémoire est
      effacée et la teinte retirée en fondu (l'événement visitorConsentCollected ne part qu'au changement, un
      consentement expiré ou retiré ailleurs garderait sinon la teinte). Sur « visitorConsentCollected » : refus, on
      efface ; accord, on pose le marqueur et on note la page en cours.
      Régions sans bandeau : l'API répond « permis » par défaut (choix de politique de la cliente, voir le rapport).
   2. Signaux (localStorage « selv-mem » = {v:1, ev:[[clé, poids, horodatage]…], top, exp} ou {v:1, off:1}) :
      fiche vue +1 (parfum lu sur la section .sf : style « --drop: var(--scent-<clé>) », sinon le titre et le handle avec
      les règles de snippets/lq-scent.liquid ; une fois par produit et par 30 min), ajout au panier +3 (événement
      cart:added de Flow : res.items ou res ; titre puis handle ; chandelles « effil… » ignorées).
      Score = Σ poids × 0,5^(âge en jours / 21) ; 40 signaux au plus, 90 jours au plus. Parfum retenu (top) si le 1er
      score vaut au moins 1,8 (deux signaux récents) et 1,5 fois le 2e ; exp = dernier signal + 60 jours (Safari peut effacer localStorage après
      7 jours sans visite : 60 jours est un plafond, pas une promesse). Seuls Nordique, Boréal, Quartz, Luv, Verbena et
      Blomst comptent. La teinte change au chargement suivant, jamais sous les yeux.
   3. Accueil : rangée « Pour ton rituel [parfum] » (sections/selv-memoire + selv-memoire-liste), voir ces fichiers.
   4. « Oublier » ([data-selv-mem-forget], rangée et pied de page) : {v:1, off:1}, teinte retirée en fondu (sauf
      « réduire les animations »), focus rendu à un repère voisin, message « C'est oublié. » (zone role=status de
      SelvLiquid.toast).
   5. Autres pages ouvertes : un « Oublier » ou un refus de consentement fait ailleurs (autre onglet : événement
      storage ; retour arrière depuis le bfcache : pageshow persisted, qui relit aussi le consentement) retire la
      teinte, la ligne du pied de page et la rangée. Jamais l'inverse : une teinte n'est jamais posée en cours de visite.
   API : window.SelvMemoire = { scent(), forget() }. */
(() => {
  'use strict';
  if (window.SelvMemoire) return;
  const d = document;
  const h = d.documentElement;
  const KEY = 'selv-mem';
  const SEEN = 'selv-mem-vus';
  const DAY = 864e5;
  // seuil du parfum retenu : « 2 signaux » (deux fiches vues, ou un ajout) ; 1,8 et non 2, car la décroissance retire
  // déjà quelques millièmes à deux fiches vues à quelques secondes d'intervalle (deux vues restent assez à 6 jours près)
  const MIN = 1.8;
  const SIX = ['nordique', 'boreal', 'quartz', 'luv', 'verbena', 'blomst'];
  // même ordre que snippets/lq-scent.liquid : le 1er mot-clé trouvé gagne (« Boho » ne compte pas, mais il doit
  // gagner sur un parfum cité plus loin dans le même titre)
  const ALL = SIX.concat(['boho', 'orangerie', 'floreale', 'bavaroise', 'montana', 'mystique', 'bouquet']);
  const LABEL = { nordique: 'Nordique', boreal: 'Boréal', quartz: 'Quartz', luv: 'Luv', verbena: 'Verbena', blomst: 'Blomst' };
  const en = (h.lang || '').toLowerCase().startsWith('en');
  const TXT = en ? { forgot: 'Forgotten.' } : { forgot: 'C’est oublié.' };
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 200));
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Stockage : chaque accès est protégé (navigation privée, données bloquées, aperçus) */
  const read = () => {
    try {
      const s = JSON.parse(localStorage.getItem(KEY) || 'null');
      return s && s.v === 1 ? s : null;
    } catch (e) { return null; }
  };
  const write = (s) => { try { localStorage.setItem(KEY, JSON.stringify(s)); return true; } catch (e) { return false; } };
  const wipe = () => {
    try { localStorage.removeItem(KEY); } catch (e) { /* rien */ }
    try { sessionStorage.removeItem(SEEN); } catch (e) { /* rien */ }
  };

  /* Parfum d'un titre ou d'un handle (règles de lq-scent : titre, puis handle) */
  const norm = (t) => String(t || '').toLowerCase().replace(/[éÉ]/g, 'e');
  const scentOf = (title, handle) => {
    const t = norm(title);
    if (t.includes('effil')) return '';
    let k = ALL.find((x) => t.includes(x));
    if (!k && handle) {
      const hh = norm(handle).replace(/-/g, ' ');
      k = ALL.find((x) => hh.includes(x));
    }
    return k && SIX.includes(k) ? k : '';
  };

  /* Score et parfum retenu */
  const leader = (s, now) => {
    const sc = {};
    (s.ev || []).forEach((e) => {
      if (!Array.isArray(e) || !SIX.includes(e[0])) return;
      sc[e[0]] = (sc[e[0]] || 0) + (+e[1] || 0) * Math.pow(0.5, Math.max(0, now - (+e[2] || 0)) / DAY / 21);
    });
    const r = Object.keys(sc).map((k) => [k, sc[k]]).sort((a, b) => b[1] - a[1]);
    if (!r.length) return '';
    const v2 = r[1] ? r[1][1] : 0;
    return r[0][1] >= MIN && r[0][1] >= 1.5 * v2 ? r[0][0] : '';
  };
  const tidy = (s, now) => {
    s.ev = (Array.isArray(s.ev) ? s.ev : []).filter((e) => Array.isArray(e) && now - (+e[2] || 0) < 90 * DAY).slice(-40);
    s.top = leader(s, now);
    const last = s.ev.length ? +s.ev[s.ev.length - 1][2] : 0;
    s.exp = last ? last + 60 * DAY : 0;
    return s;
  };

  /* Consentement : null = pas encore connu, true / false sinon */
  let allowed = null;
  const pending = [];
  // done : appelé une fois le signal réellement écrit (sert à retenir la fiche vue pour 30 min)
  const record = (k, w, done) => {
    if (!k) return;
    if (allowed === null) { pending.push([k, w, done]); return; }
    if (!allowed) return;
    const s = read();
    if (!s || s.off) return;
    const now = Date.now();
    s.ev = Array.isArray(s.ev) ? s.ev : [];
    s.ev.push([k, w, now]);
    if (write(tidy(s, now)) && done) done();
  };

  /* Teinte retirée (oubli, consentement absent) : fondu de .6 s sur les surfaces, sauf « réduire les animations » */
  let fadeTimer = 0;
  const unpaint = () => {
    if (!h.hasAttribute('data-selv-mem')) return;
    if (!reduce) {
      h.classList.add('selv-mem-fade');
      clearTimeout(fadeTimer);
      fadeTimer = setTimeout(() => h.classList.remove('selv-mem-fade'), 700);
    }
    h.removeAttribute('data-selv-mem');
    h.removeAttribute('data-selv-mem-src');
  };

  /* Fiche vue : une fois par produit et par 30 min (sessionStorage) */
  const viewNow = () => {
    const sf = d.querySelector('#MainContent section.sf[data-sf]') || d.querySelector('section.sf[data-sf]');
    if (!sf) return;
    const m = /--scent-([a-z-]+)/.exec(sf.getAttribute('style') || '');
    const handle = sf.getAttribute('data-product-handle') || '';
    const k = m ? (SIX.includes(m[1]) ? m[1] : '') : scentOf(sf.getAttribute('aria-label'), handle);
    if (!k) return;
    const id = handle || k;
    const seen = () => { try { return JSON.parse(sessionStorage.getItem(SEEN) || '{}') || {}; } catch (e) { return {}; } };
    const at = seen()[id];
    if (at && Date.now() - at < 30 * 6e4) return;
    record(k, 1, () => {
      const v = seen();
      v[id] = Date.now();
      try { sessionStorage.setItem(SEEN, JSON.stringify(v)); } catch (e) { /* rien */ }
    });
  };

  const decide = (ok) => {
    allowed = !!ok;
    if (!allowed) {
      pending.length = 0;
      if (read()) wipe();
      unpaint();
      return;
    }
    let s = read();
    if (!s) { s = { v: 1, ev: [] }; write(s); }
    if (s.off) { pending.length = 0; return; }
    write(tidy(s, Date.now()));
    pending.splice(0).forEach(([k, w, done]) => record(k, w, done));
  };

  const api = () => window.Shopify && window.Shopify.customerPrivacy;
  const check = () => {
    const c = api();
    if (!c || typeof c.preferencesProcessingAllowed !== 'function') return;
    let ok = false;
    try { ok = !!c.preferencesProcessingAllowed(); } catch (e) { ok = false; }
    decide(ok);
  };
  const loadConsent = () => {
    if (api()) { check(); return; }
    const S = window.Shopify;
    if (!S || typeof S.loadFeatures !== 'function') return;
    try {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err) check(); });
    } catch (e) { /* sans API : rien n'est écrit */ }
  };
  d.addEventListener('visitorConsentCollected', (e) => {
    const det = (e && e.detail) || {};
    if (typeof det.preferencesAllowed !== 'boolean') { check(); return; }
    decide(det.preferencesAllowed);
    if (det.preferencesAllowed) viewNow();
  });

  /* Mémoire effacée ou oubliée ailleurs (autre onglet, page quittée puis rendue par le bfcache) : on relit le
     stockage et on retire ce qui la montre encore ici ; on ne repeint jamais (pas de teinte sous les yeux) */
  const sync = () => {
    const s = read();
    if (s && !s.off) return;
    pending.length = 0;
    unpaint();
    d.querySelectorAll('[data-selv-mem-row]').forEach((r) => { r.hidden = true; });
  };
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    sync();
    if (api()) check();
  });
  window.addEventListener('storage', (e) => {
    try { if (e.storageArea && e.storageArea !== localStorage) return; } catch (err) { return; }
    if (e.key === KEY || e.key === null) sync();
  });

  /* Ajout au panier (+3 par parfum et par ajout) */
  const bus = window.eventBus;
  if (bus && typeof bus.on === 'function') {
    bus.on('cart:added', (res) => {
      const items = (res && (Array.isArray(res.items) ? res.items : [res])) || [];
      const keys = new Set();
      items.forEach((it) => { if (it) { const k = scentOf(it.product_title || it.title, it.handle); if (k) keys.add(k); } });
      keys.forEach((k) => record(k, 3));
    });
  }

  /* Rangée « Pour ton rituel [parfum] » de l'accueil */
  const handleOf = (href) => {
    const m = /\/products\/([^/?#]+)/.exec(href || '');
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  };
  const row = async () => {
    const shell = d.querySelector('[data-selv-mem-row]');
    const k = h.getAttribute('data-selv-mem');
    if (!shell || !shell.hidden || !SIX.includes(k)) return;
    let map = {};
    try { map = JSON.parse(shell.getAttribute('data-map') || '{}') || {}; } catch (e) { return; }
    const c = map[k];
    if (!c || !c.h) return;
    try {
      const base = rootUrl();
      const [rl, rc] = await Promise.all([
        fetch(`${base}collections/${encodeURIComponent(c.h)}?section_id=selv-memoire-liste`, { headers: { Accept: 'text/html' } }),
        fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } }).catch(() => null),
      ]);
      if (!rl.ok) return;
      const doc = new DOMParser().parseFromString(await rl.text(), 'text/html');
      const items = Array.from(doc.querySelectorAll('[data-selv-mem-list] > li[data-handle]'));
      const skip = new Set();
      if (rc && rc.ok) {
        try { ((await rc.json()).items || []).forEach((it) => skip.add(it.handle)); } catch (e) { /* panier illisible : rien à retirer */ }
      }
      d.querySelectorAll('.slq-best a[href*="/products/"], [data-slq-etat-panel]:not([hidden]) a[href*="/products/"]').forEach((a) => {
        const hd = handleOf(a.getAttribute('href'));
        if (hd) skip.add(hd);
      });
      let keep = items.filter((li) => !skip.has(li.getAttribute('data-handle')));
      if (keep.length < 2) return;
      keep = keep.slice(0, keep.length >= 4 ? 4 : 2);
      // la mémoire a pu être oubliée entre-temps ; et jamais d'insertion à l'écran ou au-dessus (aucun décalage visible)
      if (h.getAttribute('data-selv-mem') !== k) return;
      const anchor = shell.parentElement || shell;
      if (anchor.getBoundingClientRect().top <= window.innerHeight) return;
      const grid = shell.querySelector('[data-selv-mem-grid]');
      if (!grid) return;
      grid.replaceChildren(...keep.map((li) => d.importNode(li, true)));
      shell.classList.toggle('is-duo', keep.length === 2);
      shell.querySelectorAll('[data-selv-mem-label]').forEach((el) => { el.textContent = LABEL[k]; });
      const link = shell.querySelector('[data-selv-mem-link]');
      if (link && c.u) link.setAttribute('href', c.u);
      shell.hidden = false;
      const L = window.SelvLiquid;
      if (L && typeof L.init === 'function') L.init(shell);
      if (L && typeof L.expire === 'function') L.expire();
    } catch (e) { /* toute erreur : la rangée reste cachée */ }
  };

  /* Oublier */
  const forget = (btn) => {
    write({ v: 1, off: 1 });
    try { sessionStorage.removeItem(SEEN); } catch (e) { /* rien */ }
    pending.length = 0;
    const shell = btn && btn.closest ? btn.closest('[data-selv-mem-row]') : null;
    // Repère qui reçoit le focus : la section suivante (rangée) ou la cible donnée par le bouton (pied de page)
    let target = null;
    if (shell) {
      let sec = (shell.parentElement || shell).nextElementSibling;
      while (sec && !target) {
        target = sec.querySelector('h2, h3');
        sec = sec.nextElementSibling;
      }
    } else if (btn && btn.getAttribute('data-selv-mem-after')) {
      const scope = btn.closest('footer') || d;
      target = scope.querySelector(btn.getAttribute('data-selv-mem-after'));
    }
    target = target || d.getElementById('MainContent');
    unpaint();
    d.querySelectorAll('[data-selv-mem-row]').forEach((r) => { r.hidden = true; });
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: !shell });
    }
    const L = window.SelvLiquid;
    if (L && typeof L.toast === 'function') L.toast(TXT.forgot);
  };
  d.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-selv-mem-forget]') : null;
    if (!btn) return;
    e.preventDefault();
    forget(btn);
  });

  window.SelvMemoire = { scent: () => h.getAttribute('data-selv-mem') || '', forget: () => forget(null) };

  const start = () => idle(() => {
    loadConsent();
    viewNow();
    row();
  });
  if (d.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
